// src/helpers/PdfDocument.jsx
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  Link,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf",
      fontWeight: "bold",
    },
  ],
});

const FS_BRAND = "#1a1f2e";
const FS_BRAND_MID = "#252c3d";
const FS_YELLOW = "#ffeb66";
const FS_GREEN = "#22c55e";
const FS_AMBER = "#f59e0b";
const FS_RED = "#ef4444";
const CARD_BORDER = "#e5e7eb";
const BG_SUBTLE = "#f9fafb";
const BG_NAVY_TINT = "rgba(26, 31, 46, 0.04)";

const FSPILL_LIGHTGREENBG = "rgba(0, 180, 60, 0.12)";
const FSPILL_LIGHTGREENFG = "rgba(0, 120, 40, 1)";
const FSPILL_GREENBG = "rgba(120, 200, 60, 0.16)";
const FSPILL_GREENFG = "rgba(70, 120, 25, 1)";
const FSPILL_AMBERBG = "rgba(255, 168, 0, 0.18)";
const FSPILL_AMBERFG = "rgba(140, 85, 0, 1)";
const FSRPILL_REDBG = "rgba(255, 95, 55, 0.18)";
const FSRPILL_REDFG = "rgba(160, 40, 15, 1)";
const FSRPILL_DARKREDBG = "rgba(150, 0, 0, 0.22)";
const FSRPILL_DARKREDFG = "rgba(120, 0, 0, 1)";

const C = {
  navy: FS_BRAND,
  navy2: FS_BRAND_MID,
  blue: FS_BRAND_MID,
  yellow: FS_YELLOW,
  green: FS_GREEN,
  red: FS_RED,
  orange: FS_AMBER,
  grey: "#6b7280",
  grey2: "#9ca3af",
  bg: BG_SUBTLE,
  bg2: BG_NAVY_TINT,
  bg3: "#f3f4f6",
  border: CARD_BORDER,
  text: FS_BRAND,
  text2: "#4b5563",
  white: "#ffffff",
};

const STATUS_META = {
  15: { text: "FAIL", bg: FSRPILL_DARKREDBG, fg: FSRPILL_DARKREDFG },
  14: { text: "NEEDS IMPROVEMENT", bg: FSPILL_AMBERBG, fg: FSPILL_AMBERFG },
  13: { text: "PASS", bg: FSPILL_LIGHTGREENBG, fg: FSPILL_LIGHTGREENFG },
  8: { text: "COMPLETED", bg: BG_NAVY_TINT, fg: FS_BRAND },
  1: { text: "SUBMITTED", bg: BG_NAVY_TINT, fg: FS_BRAND },
  0: { text: "DRAFT", bg: BG_NAVY_TINT, fg: FS_BRAND },
};

const CRIT_META = {
  High: { bg: FSRPILL_REDBG, fg: FSRPILL_REDFG, accent: FS_RED },
  Medium: { bg: FSPILL_AMBERBG, fg: FSPILL_AMBERFG, accent: FS_AMBER },
  Low: { bg: FSPILL_GREENBG, fg: FSPILL_GREENFG, accent: FS_GREEN },
};

const isPresent = (v) => {
  if (v == null) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v).length > 0;
  return true;
};

const hasUserDetails = (v) => {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  return [v.firstName, v.lastName, v.email, v.company, v.phoneNumber].some(isPresent);
};

const walkModules = (modules = [], cb) => {
  modules.forEach((m) => {
    cb(m);
    if (Array.isArray(m.children)) walkModules(m.children, cb);
    if (Array.isArray(m.questions)) walkModules(m.questions, cb);
  });
};

const getModuleKey = (module) => module?.id || module?.label || module?.text;

const getModuleAnswer = (module, data = {}) => {
  if (!module) return undefined;
  if (module.type === "user_details") {
    return data[getModuleKey(module)] || data.user_details || data.userDetails;
  }
  return data[getModuleKey(module)];
};

const getUserDetails = (formStructure, data = {}) => {
  if (hasUserDetails(data.user_details)) return data.user_details;
  if (hasUserDetails(data.userDetails)) return data.userDetails;

  let fromModule = null;
  walkModules(formStructure?.modules || [], (module) => {
    if (fromModule || module.type !== "user_details") return;
    const val = data[module.id];
    if (hasUserDetails(val)) fromModule = val;
  });
  if (fromModule) return fromModule;

  const objectValues = Object.values(data || {});
  return objectValues.find(hasUserDetails) || {};
};

const clean = (value) => {
  if (value == null || value === "") return "-";
  if (Array.isArray(value)) return value.filter(isPresent).join(", ") || "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("en-NZ", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const makePdfSafeImageSrc = (uri) => {
  if (!uri || typeof uri !== "string") return uri;
  if (uri.startsWith("data:") || uri.startsWith("blob:")) return uri;
  if (uri.startsWith("http")) return `${uri}${uri.includes("?") ? "&" : "?"}pdfRender=1`;
  return uri;
};

const getImageSourceFromModule = (module) => {
  if (!module || typeof module !== "object") return "";

  return (
    module.url ||
    module.src ||
    module.imageUrl ||
    module.image_url ||
    module.logoUrl ||
    module.logo_url ||
    ""
  );
};

const getTopFormLogoSrc = (modules = []) => {
  // If the form starts with an image module, treat it as the form/client logo.
  // Example: PMG permit forms have the PMG image as the first top-level module.
  const firstModule = modules.find(Boolean);
  if (firstModule?.type === "image") {
    return getImageSourceFromModule(firstModule);
  }

  return "";
};

const stripHtml = (html = "") =>
  String(html)
    .replace(/<br\s*\/?>(?![^<]*>)/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const normCrit = (ac) => ({
  High: Array.isArray(ac?.High) ? ac.High : [],
  Medium: Array.isArray(ac?.Medium) ? ac.Medium : [],
  Low: Array.isArray(ac?.Low) ? ac.Low : [],
});

const getCritLevel = (module, option) => {
  if (!option || !module?.auditCriticality) return null;
  const crit = normCrit(module.auditCriticality);
  if (crit.High.includes(option)) return "High";
  if (crit.Medium.includes(option)) return "Medium";
  if (crit.Low.includes(option)) return "Low";
  return null;
};

const getSelMeta = (module, selection) => {
  const level = getCritLevel(module, selection);
  if (level) return CRIT_META[level];

  const val = String(selection || "").toLowerCase();
  if (["safe", "yes", "pass", "compliant"].includes(val)) {
    return { bg: FSPILL_LIGHTGREENBG, fg: FSPILL_LIGHTGREENFG, accent: FS_GREEN };
  }
  if (["at risk", "no", "fail", "failed", "non-compliant"].includes(val)) {
    return { bg: FSRPILL_REDBG, fg: FSRPILL_REDFG, accent: FS_RED };
  }
  if (["n/a", "na", "not applicable"].includes(val)) {
    return { bg: BG_NAVY_TINT, fg: C.grey, accent: C.grey2 };
  }
  return { bg: BG_NAVY_TINT, fg: FS_BRAND, accent: FS_BRAND_MID };
};

const getSelColor = (module, selection) => getSelMeta(module, selection).bg;
const getSelTextColor = (module, selection) => getSelMeta(module, selection).fg;

const computeAuditStats = (modules, responses) => {
  const stats = { total: 0, high: 0, medium: 0, low: 0 };
  walkModules(modules, (module) => {
    if (!["inspection_item", "audit_checkbox_group"].includes(module.type)) return;
    const raw = responses?.[module.id];
    const selected = module.type === "inspection_item"
      ? [raw?.selection ?? (typeof raw === "string" ? raw : null)].filter(Boolean)
      : (Array.isArray(raw) ? raw : raw ? [raw] : []);

    selected.forEach((opt) => {
      stats.total += 1;
      const level = getCritLevel(module, opt);
      if (level === "High") stats.high += 1;
      if (level === "Medium") stats.medium += 1;
      if (level === "Low") stats.low += 1;
    });
  });
  return stats;
};

const getFormTypeBadge = (formType) => {
  const t = String(formType || "").toLowerCase();
  if (t === "audit") return "AUDIT REPORT";
  if (t === "permit") return "PERMIT";
  if (t === "induction" || t === "company induction") return "INDUCTION";
  return String(formType || "FORM RESPONSE").toUpperCase();
};

const getCompletedByLabel = (formType) => {
  const t = String(formType || "").toLowerCase();
  if (t === "permit") return "Permit Holder";
  if (t === "induction" || t === "company induction") return "Inducted User";
  return "Completed By";
};

const matchesStatus = (module, responseStatus) => {
  const status = Number(responseStatus);
  if (module?.approvalRequired === true && status === 1) return false;
  const v = module?.visibleWhenStatus;
  if (v == null) return true;
  const arr = Array.isArray(v) ? v : [v];
  if (arr.includes(0)) return true;
  return arr.includes(status);
};

const matchesVisibilityCondition = (module, data = {}) => {
  const rule = module?.visibilityCondition;
  if (!rule || typeof rule !== "object") return true;
  const actual = data[rule.moduleId];
  const actualArr = Array.isArray(actual) ? actual : actual != null ? [actual] : [];

  if (rule.operator === "equals") return actualArr.includes(rule.value);
  if (rule.operator === "not_equals") return !actualArr.includes(rule.value);
  if (rule.operator === "contains") return actualArr.includes(rule.value);
  return true;
};

const moduleHasRenderableContent = (module, data = {}, signatures = [], ctx = {}) => {
  if (!module || !matchesStatus(module, ctx.responseStatus) || !matchesVisibilityCondition(module, data)) return false;

  const answer = getModuleAnswer(module, data);

  if (module.type === "section") {
    return (module.children || []).some((child) => moduleHasRenderableContent(child, data, signatures, ctx));
  }

  if (module.type === "banner") {
    return (module.children || []).some((child) => isPresent(child.text));
  }

  if (["header", "subheader", "paragraph"].includes(module.type)) {
    return isPresent(module.text) || isPresent(stripHtml(module.content));
  }

  if (["short_answer", "long_answer", "email", "radio_button_group", "dropdown_select", "email_dropdown"].includes(module.type)) {
    return isPresent(answer);
  }

  if (["datetime", "date_range", "time_range", "double_short_answer"].includes(module.type)) {
    return isPresent(answer);
  }

  if (["checkbox", "checkbox_group", "custom_checkbox_group", "audit_checkbox_group"].includes(module.type)) {
    return isPresent(answer);
  }

  if (module.type === "inspection_item") {
    return isPresent(answer?.selection) || isPresent(answer?.notes) || isPresent(answer?.media);
  }

  if (["image_upload", "photo_gallery"].includes(module.type)) {
    return Array.isArray(answer) && answer.length > 0;
  }

  if (module.type === "signature") {
    return signatures.some((s) => s.module_id === module.id && s.signature_data);
  }

  if (module.type === "table") {
    return Array.isArray(answer) && answer.length > 0;
  }

  if (module.type === "user_details") return hasUserDetails(answer);

  if (module.type === "dynamic_question_group") {
    return (module.questions || []).some((q) => isPresent(data[q.id]));
  }

  if (module.type === "user_multi_select") return isPresent(answer);
  if (module.type === "site_information_viewer") return Array.isArray(data.siteInformation) && data.siteInformation.length > 0;

  // Uploaded documents live under data.documents[moduleId] as a URL string.
  if (module.type === "custom_document_uploader") {
    const doc = data.documents?.[module.id];
    return typeof doc === "string" && doc.trim() !== "";
  }

  // Static link buttons (e.g. "View WHS Permit Safeguards") carry their URL on
  // the module itself, independent of the response.
  if (module.type === "rectangle_link_button") {
    return typeof module.url === "string" && module.url.trim() !== "";
  }

  // Do not print empty/custom uploader shell modules.
  return false;
};

const firstAnsweredDate = (modules, data) => {
  let found = "";
  walkModules(modules || [], (module) => {
    if (found || module.type !== "datetime") return;
    const value = data?.[module.id];
    if (value) found = value;
  });
  return found;
};

const COVER_NAME_DETAIL_LABELS = [
  "project name",
  "project number",
  "project no",
  "site name",
  "site number",
  "site no",
  "property name",
  "property number",
  "property no",
  "building name",
  "building number",
  "building no",
];

const COVER_ADDRESS_DETAIL_LABELS = [
  "project address",
  "site address",
  "property address",
  "building address",
  "physical address",
  "street address",
  "job address",
  "work address",
  "location address",
  "address",
];

const collectCoverDetailFields = (modules = [], data = {}, labelTerms = []) => {
  const fields = [];
  walkModules(modules, (module) => {
    if (fields.length >= 6 || module?.type !== "short_answer") return;
    const label = String(module.label || "").toLowerCase().replace(/\s+/g, " ").trim();
    if (!label) return;

    const matches = labelTerms.some((term) => label.includes(term));
    if (!matches) return;

    const value = getModuleAnswer(module, data);
    if (!isPresent(value)) return;
    fields.push({ label: module.label, value });
  });
  return fields;
};

const getCoverDetailFields = (modules = [], data = {}) => {
  const nameFields = collectCoverDetailFields(modules, data, COVER_NAME_DETAIL_LABELS);
  if (nameFields.length) return nameFields;

  // If the form does not capture a useful name/number field, fall back to address fields
  // so the cover still identifies the project/site/property clearly.
  return collectCoverDetailFields(modules, data, COVER_ADDRESS_DETAIL_LABELS);
};

const getScoreMeta = (score) => {
  const n = Number(score);
  if (!Number.isFinite(n)) return { bg: BG_NAVY_TINT, fg: FS_BRAND };
  if (n >= 90) return { bg: FSPILL_LIGHTGREENBG, fg: FSPILL_LIGHTGREENFG };
  if (n >= 80) return { bg: FSPILL_GREENBG, fg: FSPILL_GREENFG };
  if (n >= 60) return { bg: FSPILL_AMBERBG, fg: FSPILL_AMBERFG };
  return { bg: FSRPILL_REDBG, fg: FSRPILL_REDFG };
};

const S = StyleSheet.create({
  page: {
    fontFamily: "Roboto",
    fontSize: 10,
    color: C.text,
    backgroundColor: C.white,
    paddingBottom: 46,
  },
  coverTop: {
    backgroundColor: C.navy,
    paddingTop: 28,
    paddingHorizontal: 30,
    paddingBottom: 24,
  },
  coverTopRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  logoBox: {
    width: 104,
    height: 48,
    borderWidth: 1,
    borderColor: C.yellow,
    borderStyle: "solid",
    justifyContent: "center",
    alignItems: "center",
  },
  logoImageBox: {
    backgroundColor: C.white,
    borderColor: C.white,
    padding: 5,
  },
  logoImage: {
    width: 94,
    height: 38,
    objectFit: "contain",
  },
  logoText: { color: C.yellow, fontSize: 8, fontWeight: "bold", letterSpacing: 1.2 },
  typeBadge: {
    backgroundColor: C.yellow,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 3,
  },
  typeBadgeText: { color: C.navy, fontSize: 8, fontWeight: "bold", letterSpacing: 1.3 },
  coverTitle: { marginTop: 46, color: C.white, fontSize: 30, fontWeight: "bold", lineHeight: 1.08 },
  coverSubtitle: { marginTop: 8, color: "#cbd5e1", fontSize: 12 },
  accentBar: { height: 6, backgroundColor: C.yellow },
  coverBody: { padding: 30 },
  summaryGrid: { flexDirection: "row", gap: 12, marginBottom: 18 },
  coverDetailGrid: { flexDirection: "row", flexWrap: "wrap", gap: 10, marginBottom: 18 },
  coverDetailCard: {
    width: "31.8%",
    backgroundColor: BG_NAVY_TINT,
    borderWidth: 1,
    borderColor: C.border,
    borderStyle: "solid",
    borderRadius: 6,
    padding: 10,
  },
  summaryCard: {
    flex: 1,
    backgroundColor: C.bg,
    borderWidth: 1,
    borderColor: C.border,
    borderStyle: "solid",
    borderRadius: 6,
    padding: 12,
  },
  summaryLabel: { fontSize: 7.5, color: C.grey, fontWeight: "bold", letterSpacing: 0.7, marginBottom: 5 },
  summaryValue: { fontSize: 11, color: C.text, fontWeight: "bold", lineHeight: 1.25 },
  summarySub: { fontSize: 9, color: C.text2, marginTop: 3, lineHeight: 1.25 },
  scoreRow: { flexDirection: "row", marginTop: 12, gap: 10 },
  scoreTile: {
    flex: 1,
    borderRadius: 6,
    padding: 12,
    alignItems: "center",
    borderWidth: 1,
    borderColor: C.border,
    borderStyle: "solid",
  },
  scoreBig: { fontSize: 24, fontWeight: "bold", lineHeight: 1 },
  scoreSmall: { fontSize: 7.5, marginTop: 3, letterSpacing: 0.5, fontWeight: "bold" },
  pageHeader: {
    backgroundColor: C.navy,
    paddingHorizontal: 24,
    paddingVertical: 10,
    // The gap below the header lives here, not as paddingTop on `body`. A
    // paddingTop on the wrapping body offsets its first child on *every* page,
    // which makes react-pdf's "break to improve presence" heuristic fire
    // forever when a block lands within a few points of the page boundary.
    marginBottom: 18,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  pageHeaderTitle: { color: C.white, fontSize: 9, fontWeight: "bold", flex: 1, paddingRight: 10 },
  pageHeaderBadge: { backgroundColor: C.yellow, borderRadius: 3, paddingHorizontal: 8, paddingVertical: 3 },
  pageHeaderBadgeText: { color: C.navy, fontSize: 7, fontWeight: "bold" },
  body: { paddingHorizontal: 24 },
  footer: {
    position: "absolute",
    left: 24,
    right: 24,
    bottom: 16,
    borderTopWidth: 0.5,
    borderTopColor: C.border,
    borderTopStyle: "solid",
    paddingTop: 6,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: { color: C.grey2, fontSize: 7.5 },
  moduleBlock: { marginBottom: 8 },
  section: {
    borderWidth: 1,
    borderColor: C.border,
    borderStyle: "solid",
    borderRadius: 6,
    marginBottom: 12,
    overflow: "hidden",
  },
  sectionHeader: { backgroundColor: C.bg2, paddingVertical: 8, paddingHorizontal: 10 },
  sectionHeaderText: { fontSize: 11, fontWeight: "bold", color: C.navy },
  sectionBody: { padding: 10 },
  banner: { backgroundColor: C.bg3, borderRadius: 5, paddingVertical: 7, paddingHorizontal: 9, marginBottom: 6 },
  headerText: { color: C.navy, fontSize: 14, fontWeight: "bold", lineHeight: 1.25 },
  subheaderText: { color: C.navy2, fontSize: 10.5, fontWeight: "bold", lineHeight: 1.3 },
  fieldCard: {
    borderWidth: 1,
    borderColor: C.border,
    borderStyle: "solid",
    backgroundColor: C.white,
    borderRadius: 5,
    padding: 8,
  },
  fieldLabel: { fontSize: 7.5, color: C.grey, fontWeight: "bold", marginBottom: 3, letterSpacing: 0.3 },
  fieldValue: { fontSize: 9.5, color: C.text, lineHeight: 1.35 },
  paragraph: { fontSize: 9, color: C.text2, lineHeight: 1.35 },
  statusRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: 0.5,
    borderBottomColor: C.border,
    borderBottomStyle: "solid",
    paddingVertical: 7,
  },
  statusLabel: { flex: 1, fontSize: 9.2, color: C.text, lineHeight: 1.3, paddingRight: 10 },
  chipRow: { flexDirection: "row", flexWrap: "wrap", justifyContent: "flex-end", maxWidth: 180 },
  chip: { borderRadius: 999, paddingHorizontal: 8, paddingVertical: 3, marginLeft: 4, marginVertical: 1 },
  chipText: { color: C.white, fontSize: 7.5, fontWeight: "bold" },
  inspectionCard: {
    borderWidth: 1,
    borderColor: C.border,
    borderStyle: "solid",
    borderRadius: 6,
    padding: 9,
    backgroundColor: C.white,
  },
  inspectionTop: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" },
  inspectionLabel: { flex: 1, color: C.text, fontSize: 9.5, lineHeight: 1.35, paddingRight: 10 },
  weightBadge: { marginTop: 5, alignSelf: "flex-start", backgroundColor: C.bg2, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8 },
  weightText: { fontSize: 7, color: C.text2, fontWeight: "bold" },
  noteBox: { marginTop: 7, backgroundColor: "#fff7ed", borderLeftWidth: 3, borderLeftColor: C.orange, borderLeftStyle: "solid", padding: 7 },
  noteText: { fontSize: 8.5, color: C.text2, lineHeight: 1.35 },
  imageGrid: { flexDirection: "row", flexWrap: "wrap", marginTop: 7 },
  thumb: { width: 94, height: 74, objectFit: "cover", marginRight: 6, marginBottom: 6, borderRadius: 3 },
  sigBox: { borderWidth: 1, borderColor: C.border, borderStyle: "solid", borderRadius: 4, padding: 6, alignSelf: "flex-start" },
  sigImage: { width: 170, height: 58, objectFit: "contain" },
  table: { borderWidth: 1, borderColor: C.border, borderStyle: "solid", borderRadius: 5, overflow: "hidden" },
  tableRow: { flexDirection: "row" },
  tableHeadCell: { flex: 1, backgroundColor: C.navy, paddingVertical: 6, paddingHorizontal: 7 },
  tableHeadText: { fontSize: 8, color: C.white, fontWeight: "bold" },
  tableCell: { flex: 1, paddingVertical: 6, paddingHorizontal: 7, borderTopWidth: 0.5, borderTopColor: C.border, borderTopStyle: "solid" },
  tableText: { fontSize: 8.8, color: C.text },
  linkButton: {
    alignSelf: "flex-start",
    backgroundColor: C.navy,
    borderRadius: 4,
    paddingVertical: 7,
    paddingHorizontal: 12,
    marginVertical: 2,
  },
  linkButtonText: { color: C.white, fontSize: 9.5, fontWeight: "bold", textDecoration: "none" },
});

function Chip({ label, color, textColor }) {
  return (
    <View style={[S.chip, { backgroundColor: color || BG_NAVY_TINT }]}>
      <Text style={[S.chipText, { color: textColor || FS_BRAND }]}>{label}</Text>
    </View>
  );
}

function FieldCard({ label, children, preventBreak = false }) {
  return (
    <View style={S.fieldCard} wrap={preventBreak ? false : true}>
      {label ? <Text style={S.fieldLabel}>{label}</Text> : null}
      {children}
    </View>
  );
}

function renderChipsForOptions(module, answer) {
  const options = module.options || [];
  const selectedRaw = Array.isArray(answer) ? answer : answer ? [answer] : [];
  const selected = options.length ? selectedRaw.filter((v) => options.includes(v)) : selectedRaw;
  const display = selected.length ? selected : selectedRaw;

  return (
    <View style={S.statusRow} wrap={false}>
      <Text style={S.statusLabel}>{module.label || "Response"}</Text>
      <View style={S.chipRow}>
        {display.length ? display.map((opt, i) => (
          <Chip
            key={`${opt}-${i}`}
            label={opt}
            color={getSelColor(module, opt)}
            textColor={getSelTextColor(module, opt)}
          />
        )) : <Chip label="-" color={BG_NAVY_TINT} textColor={C.grey} />}
      </View>
    </View>
  );
}

function renderModule(module, data, signatures, ctx) {
  if (!moduleHasRenderableContent(module, data, signatures, ctx)) return null;

  const answer = getModuleAnswer(module, data);
  const key = getModuleKey(module);

  switch (module.type) {
    case "section": {
      const children = (module.children || [])
        .map((child, i) => ({ child, node: renderModule(child, data, signatures, ctx), i }))
        .filter((x) => x.node);
      if (!children.length) return null;

      return (
        <View style={S.section} wrap>
          {module.label || module.text ? (
            <View style={S.sectionHeader}>
              <Text style={S.sectionHeaderText}>{module.label || module.text}</Text>
            </View>
          ) : null}
          <View style={S.sectionBody}>
            {children.map(({ node, i }) => (
              <View key={i} style={S.moduleBlock}>{node}</View>
            ))}
          </View>
        </View>
      );
    }

    case "banner":
      return (
        <View style={S.banner} wrap={false}>
          {(module.children || []).map((child, i) => {
            if (child.type === "header") return <Text key={i} style={S.headerText}>{child.text}</Text>;
            if (child.type === "subheader") return <Text key={i} style={S.subheaderText}>{child.text}</Text>;
            return null;
          })}
        </View>
      );

    case "header":
      return <Text style={S.headerText}>{module.text}</Text>;

    case "subheader":
      return <Text style={S.subheaderText}>{module.text}</Text>;

    case "paragraph":
      return <Text style={S.paragraph}>{stripHtml(module.content)}</Text>;

    case "short_answer":
    case "long_answer":
    case "email":
    case "radio_button_group":
      return (
        <FieldCard label={module.label} preventBreak>
          <Text style={S.fieldValue}>{clean(answer)}</Text>
        </FieldCard>
      );

    case "datetime":
      return (
        <FieldCard label={module.label} preventBreak>
          <Text style={S.fieldValue}>{formatDateTime(answer)}</Text>
        </FieldCard>
      );

    case "date_range":
      return (
        <FieldCard label={module.label} preventBreak>
          <Text style={S.fieldValue}>{`${clean(answer?.startDate)} - ${clean(answer?.endDate)}`}</Text>
        </FieldCard>
      );

    case "time_range":
      return (
        <FieldCard label={module.label} preventBreak>
          <Text style={S.fieldValue}>{`${clean(answer?.startTime)} - ${clean(answer?.endTime)}`}</Text>
        </FieldCard>
      );

    case "checkbox":
      return renderChipsForOptions(module, answer === true ? ["Yes"] : answer === false ? ["No"] : ["N/A"]);

    case "checkbox_group":
    case "custom_checkbox_group":
    case "audit_checkbox_group":
      return renderChipsForOptions(module, answer);

    case "inspection_item": {
      const selection = answer?.selection ?? (typeof answer === "string" ? answer : null);
      const notes = answer?.notes || "";
      const media = Array.isArray(answer?.media) ? answer.media : [];
      const level = selection ? getCritLevel(module, selection) : null;

      return (
        <View style={S.inspectionCard} wrap={false}>
          <View style={S.inspectionTop}>
            <View style={{ flex: 1 }}>
              <Text style={S.inspectionLabel}>{module.label}</Text>
              {typeof module.weight === "number" && module.weight > 0 ? (
                <View style={S.weightBadge}><Text style={S.weightText}>{module.weight} pts</Text></View>
              ) : null}
            </View>
            <Chip label={selection || "-"} color={getSelColor(module, selection)} textColor={getSelTextColor(module, selection)} />
          </View>
          {level ? (
            <View style={[S.noteBox, { borderLeftColor: CRIT_META[level].accent }]}>
              <Text style={S.noteText}>{level} criticality response</Text>
            </View>
          ) : null}
          {notes ? (
            <View style={S.noteBox}><Text style={S.noteText}>Note: {notes}</Text></View>
          ) : null}
          {media.length ? (
            <View style={S.imageGrid} wrap={false}>
              {media.map((uri, i) => <Image key={i} src={makePdfSafeImageSrc(uri)} style={S.thumb} />)}
            </View>
          ) : null}
        </View>
      );
    }

    case "image_upload":
    case "photo_gallery":
      return (
        <FieldCard label={module.label} preventBreak>
          <View style={S.imageGrid} wrap={false}>
            {(Array.isArray(answer) ? answer : []).map((uri, i) => (
              <Image key={i} src={makePdfSafeImageSrc(uri)} style={S.thumb} />
            ))}
          </View>
        </FieldCard>
      );

    case "signature": {
      const sig = signatures.find((s) => s.module_id === module.id || s.module_id === key);
      if (!sig?.signature_data) return null;
      return (
        <FieldCard label={module.label || "Signature"} preventBreak>
          <View style={S.sigBox}><Image src={sig.signature_data} style={S.sigImage} /></View>
        </FieldCard>
      );
    }

    case "table": {
      const rows = Array.isArray(answer) ? answer : [];
      const cols = module.columns || [];
      if (!rows.length || !cols.length) return null;
      return (
        <FieldCard label={module.label} preventBreak>
          <View style={S.table}>
            <View style={S.tableRow}>
              {cols.map((col) => (
                <View key={col} style={S.tableHeadCell}><Text style={S.tableHeadText}>{col}</Text></View>
              ))}
            </View>
            {rows.map((row, r) => (
              <View key={r} style={[S.tableRow, { backgroundColor: r % 2 ? C.bg : C.white }]}>
                {cols.map((col) => (
                  <View key={col} style={S.tableCell}><Text style={S.tableText}>{clean(row[col])}</Text></View>
                ))}
              </View>
            ))}
          </View>
        </FieldCard>
      );
    }

    case "double_short_answer": {
      const ans = Array.isArray(answer?.answers) ? answer.answers : ["", ""];
      return (
        <FieldCard label={module.label}>
          <Text style={S.fieldValue}>{ans.filter(isPresent).join(" / ") || "-"}</Text>
        </FieldCard>
      );
    }

    case "dropdown_select":
    case "email_dropdown": {
      const opts = module.options || [];
      const toLabel = (val) => opts.find((o) => o.value === val)?.label || val;
      const value = Array.isArray(answer) ? answer.map(toLabel).join(", ") : toLabel(answer);
      return (
        <FieldCard label={module.label}>
          <Text style={S.fieldValue}>{clean(value)}</Text>
        </FieldCard>
      );
    }

    case "user_details": {
      const ud = answer || {};
      const name = [ud.firstName, ud.lastName].filter(Boolean).join(" ");
      // Permits track the permit holder's company via requested_to, not here.
      const company = ud.company || ctx.companyName;
      return (
        <FieldCard label={getCompletedByLabel(ctx.formType).toUpperCase()} preventBreak>
          {name ? <Text style={S.summaryValue}>{name}</Text> : null}
          {ud.email ? <Text style={S.summarySub}>{ud.email}</Text> : null}
          {company ? <Text style={S.summarySub}>{company}</Text> : null}
          {ud.phoneNumber ? <Text style={S.summarySub}>{ud.phoneNumber}</Text> : null}
        </FieldCard>
      );
    }

    case "dynamic_question_group":
      return (
        <FieldCard label={module.label}>
          {(module.questions || []).map((q) => {
            const resp = data[q.id];
            if (!isPresent(resp)) return null;
            return (
              <View key={q.id} style={{ marginBottom: 5 }}>
                <Text style={S.fieldLabel}>{q.label}</Text>
                <Text style={S.fieldValue}>{clean(resp)}</Text>
              </View>
            );
          })}
        </FieldCard>
      );

    case "user_multi_select":
      return (
        <FieldCard label={module.label} preventBreak>
          <Text style={S.fieldValue}>{clean(answer)}</Text>
        </FieldCard>
      );

    case "site_information_viewer": {
      const docs = data.siteInformation || [];
      return (
        <FieldCard label={module.label || "Site Documents"}>
          {docs.map((doc, i) => (
            <Link key={i} src={doc.uploaded_url} style={{ fontSize: 9, color: C.blue, marginBottom: 3 }}>
              {doc.title || doc.name || "Document"}
            </Link>
          ))}
        </FieldCard>
      );
    }

    case "custom_document_uploader": {
      const fileOrUrl = data.documents?.[module.id];
      if (typeof fileOrUrl !== "string" || !fileOrUrl.trim()) return null;
      const rawName = fileOrUrl.split("?")[0].split("/").pop() || "Document";
      let fileName = rawName;
      try {
        fileName = decodeURIComponent(rawName);
      } catch {
        /* keep raw name if it is not valid URI-encoded text */
      }
      return (
        <FieldCard label={module.label || "Document"} preventBreak>
          <Link src={fileOrUrl} style={{ fontSize: 9.5, color: C.blue }}>
            {fileName}
          </Link>
        </FieldCard>
      );
    }

    case "rectangle_link_button": {
      const url = module.url;
      if (typeof url !== "string" || !url.trim()) return null;
      return (
        <View style={S.linkButton} wrap={false}>
          <Link src={url} style={S.linkButtonText}>
            {module.label || "View document"}
          </Link>
        </View>
      );
    }

    default:
      return null;
  }
}

const PdfDocument = ({
  formStructure = {},
  responseData = {},
  signatures = [],
  responseStatus,
  auditScore,
  companyName,
}) => {
  const modules = formStructure.modules || [];
  const formType = formStructure.formType || "";
  const isAudit = String(formType).toLowerCase() === "audit";
  const isPermit = String(formType).toLowerCase() === "permit";
  const formTitle = formStructure.title || formStructure.type || responseData.type || "Form Response";
  const badgeLabel = getFormTypeBadge(formType);
  const statusNum = responseStatus != null ? Number(responseStatus) : null;
  const statusMeta = STATUS_META[statusNum] || null;
  const userDetails = getUserDetails(formStructure, responseData);
  const userName = [userDetails.firstName, userDetails.lastName].filter(Boolean).join(" ");
  // Permits capture the permit holder's company via `requested_to`, not in
  // user_details, so fall back to the requested_to company name.
  const displayCompany = userDetails.company || companyName;
  const completedDate = responseData.completed_at || responseData.completedAt || firstAnsweredDate(modules, responseData);
  const scoreValue = auditScore ?? responseData._auditScore;
  const auditStats = isAudit ? computeAuditStats(modules, responseData) : null;
  const coverDetailFields = getCoverDetailFields(modules, responseData);
  const coverLogoSrc = getTopFormLogoSrc(modules);
  const scoreMeta = getScoreMeta(scoreValue);
  const thresholds = formStructure?.scoring?.thresholds || {};

  const ctx = { formType, isAudit, isPermit, responseStatus, companyName };
  const modulesToRender = modules.filter((m) => moduleHasRenderableContent(m, responseData, signatures, ctx));

  return (
    <Document>
      <Page size="A4" style={S.page}>
        <View style={S.coverTop}>
          <View style={S.coverTopRow}>
            <View style={[S.logoBox, coverLogoSrc ? S.logoImageBox : null]}>
              {coverLogoSrc ? (
                <Image src={makePdfSafeImageSrc(coverLogoSrc)} style={S.logoImage} />
              ) : (
                <Text style={S.logoText}>YOUR LOGO</Text>
              )}
            </View>
            <View style={S.typeBadge}><Text style={S.typeBadgeText}>{badgeLabel}</Text></View>
          </View>
          <Text style={S.coverTitle}>{formTitle}</Text>
          <Text style={S.coverSubtitle}>{formStructure.type || formType || responseData.type || "Form Response"}</Text>
        </View>
        <View style={S.accentBar} />

        <View style={S.coverBody}>
          <View style={S.summaryGrid}>
            <View style={S.summaryCard}>
              <Text style={S.summaryLabel}>{getCompletedByLabel(formType).toUpperCase()}</Text>
              <Text style={S.summaryValue}>{userName || "-"}</Text>
              {userDetails.email ? <Text style={S.summarySub}>{userDetails.email}</Text> : null}
              {displayCompany ? <Text style={S.summarySub}>{displayCompany}</Text> : null}
              {userDetails.phoneNumber ? <Text style={S.summarySub}>{userDetails.phoneNumber}</Text> : null}
            </View>
            <View style={S.summaryCard}>
              <Text style={S.summaryLabel}>DOCUMENT DETAILS</Text>
              <Text style={S.summarySub}>Date Completed</Text>
              <Text style={S.summaryValue}>{formatDateTime(completedDate)}</Text>
              <Text style={S.summarySub}>Generated {formatDateTime(new Date().toISOString())}</Text>
            </View>
          </View>

          {coverDetailFields.length ? (
            <View style={S.coverDetailGrid}>
              {coverDetailFields.map((field, i) => (
                <View key={`${field.label}-${i}`} style={S.coverDetailCard} wrap={false}>
                  <Text style={S.summaryLabel}>{String(field.label).toUpperCase()}</Text>
                  <Text style={S.summaryValue}>{clean(field.value)}</Text>
                </View>
              ))}
            </View>
          ) : null}

          {(statusMeta || (isAudit && scoreValue != null)) ? (
            <View style={S.scoreRow}>
              {statusMeta ? (
                <View style={[S.scoreTile, { backgroundColor: statusMeta.bg }]}>
                  <Text style={[S.scoreBig, { color: statusMeta.fg }]}>{statusMeta.text}</Text>
                  <Text style={[S.scoreSmall, { color: statusMeta.fg }]}>STATUS</Text>
                </View>
              ) : null}
              {isAudit && scoreValue != null ? (
                <View style={[S.scoreTile, { backgroundColor: scoreMeta.bg }]}>
                  <Text style={[S.scoreBig, { color: scoreMeta.fg }]}>{scoreValue}%</Text>
                  <Text style={[S.scoreSmall, { color: scoreMeta.fg }]}>AUDIT SCORE</Text>
                </View>
              ) : null}
            </View>
          ) : null}

          {isAudit && auditStats?.total ? (
            <View style={S.scoreRow}>
              <View style={[S.scoreTile, { backgroundColor: CRIT_META.High.bg }]}><Text style={[S.scoreBig, { color: CRIT_META.High.fg }]}>{auditStats.high}</Text><Text style={[S.scoreSmall, { color: CRIT_META.High.fg }]}>HIGH</Text></View>
              <View style={[S.scoreTile, { backgroundColor: CRIT_META.Medium.bg }]}><Text style={[S.scoreBig, { color: CRIT_META.Medium.fg }]}>{auditStats.medium}</Text><Text style={[S.scoreSmall, { color: CRIT_META.Medium.fg }]}>MEDIUM</Text></View>
              <View style={[S.scoreTile, { backgroundColor: CRIT_META.Low.bg }]}><Text style={[S.scoreBig, { color: CRIT_META.Low.fg }]}>{auditStats.low}</Text><Text style={[S.scoreSmall, { color: CRIT_META.Low.fg }]}>LOW</Text></View>
            </View>
          ) : null}

          {isAudit && (thresholds.fail != null || thresholds.needsImprovement != null) ? (
            <View style={S.scoreRow}>
              {thresholds.fail != null ? (
                <View style={[S.scoreTile, { backgroundColor: FSRPILL_REDBG }]}>
                  <Text style={[S.scoreBig, { color: FSRPILL_REDFG }]}>&lt;{thresholds.fail}%</Text>
                  <Text style={[S.scoreSmall, { color: FSRPILL_REDFG }]}>FAIL THRESHOLD</Text>
                </View>
              ) : null}
              {thresholds.needsImprovement != null ? (
                <View style={[S.scoreTile, { backgroundColor: FSPILL_AMBERBG }]}>
                  <Text style={[S.scoreBig, { color: FSPILL_AMBERFG }]}>&lt;{thresholds.needsImprovement}%</Text>
                  <Text style={[S.scoreSmall, { color: FSPILL_AMBERFG }]}>NEEDS IMPROVEMENT</Text>
                </View>
              ) : null}
            </View>
          ) : null}
        </View>
      </Page>

      <Page size="A4" style={S.page} wrap>
        <View style={S.pageHeader} fixed>
          <Text style={S.pageHeaderTitle} numberOfLines={1}>{formTitle}</Text>
          <View style={S.pageHeaderBadge}><Text style={S.pageHeaderBadgeText}>{badgeLabel}</Text></View>
        </View>

        <View style={S.body}>
          {modulesToRender.map((module, i) => (
            <View key={`${module.id || module.label || module.type}-${i}`} style={S.moduleBlock}>
              {renderModule(module, responseData, signatures, ctx)}
            </View>
          ))}
        </View>

        <View style={S.footer} fixed>
          <Text style={S.footerText}>{formTitle}</Text>
          <Text style={S.footerText} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default PdfDocument;
